import moment from 'moment';
import { html, TemplateResult } from 'lit';
import { BreachPathIssueRowData } from '../types';
import { translateFn } from 'apps/webcomponents/src/utils/translator.helper';
import { AffectedDeviceType, DEVICES_CLASS, OS_CLASS } from 'apps/webcomponents/src/riskManagement/common/constants';
import { BaseWebComponentTranslator } from 'apps/webcomponents/src/framework/BaseWebComponentTranslator';
import { FontSizes, FontWeights } from 'apps/webcomponents/src/types/common/types';
import { OS_TYPE } from 'apps/webcomponents/src/riskManagement/common/constants';
import { getBulletColorForScore } from 'apps/webcomponents/src/riskManagement/common/utils';
import { getTextForScore } from 'apps/webcomponents/src/riskManagement/common/customRenderers';
import { ActionsColumnValues } from 'apps/webcomponents/src/riskManagement/common/constants';
import { BreachPathEvents, BreachPathIssueNameKey } from '../../common/types';
import { selectColumnRenderer } from 'apps/webcomponents/src/riskManagement/common/customRenderers';
import { breachPathIssueNameMapping } from '../../common/utils';

function renderLabeledContentWithEllipsisCopy(value: string): TemplateResult {
  return html`
    <div class="w-full d-flex align-items-center copy-labeled-content">
      <gz-tooltip
        class="flex-1"
        isEllipsis
        tooltipText="${value}"
      >
        <span>${value}</span>
      </gz-tooltip>
      <gz-copy-to-clipboard
        class="copy-icon"
        confirmationMessage="${translateFn('Copied')}"
        tooltipText="${translateFn('Copy to clipboard')}"
        clipboardMessage="${value}"
        style="width: 36px; flex-shrink: 0;"
      ></gz-copy-to-clipboard>
    </div>
  `;
}

export function issueNameRenderer(rowData: { item: BreachPathIssueRowData }): TemplateResult {
  return html`
    <span class="cell-label" style="color: var(--blue-layout-400-primary);">
      <a href="${`/#!/risk-management-breach-path/graph/${rowData.item._id}`}" style="color: var(--blue-layout-400-primary); text-decoration: none;">
        ${breachPathIssueNameMapping[rowData.item.name] ?? breachPathIssueNameMapping[BreachPathIssueNameKey.Unknown]}
      </a>
    </span>
  `;
}

export function resourceNameRenderer(rowData: { item: BreachPathIssueRowData }): TemplateResult {
  return html`<span class="cell-label">${renderLabeledContentWithEllipsisCopy(rowData.item.mainResource.resourceName)}</span>`;
}

export function resourceRiskScoreRenderer(rowData: { item: BreachPathIssueRowData }): TemplateResult {
  if (!rowData.item.scoreBreakdown) {
    return html`<span class="cell-label">${'-'}</span>`;
  }

  return html`
    <span class="cell-label">
      <gz-bullet color="${getBulletColorForScore(rowData.item.scoreBreakdown.finalScore)}">
        <gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}">
          ${getTextForScore(rowData.item.scoreBreakdown.finalScore)} (${rowData.item.scoreBreakdown.finalScore}%)
        </gz-text>
      </gz-bullet>
    </span>
  `;
}

export function resourceIdRenderer(rowData: { item: BreachPathIssueRowData }): TemplateResult {
  return html`<span class="cell-label">${renderLabeledContentWithEllipsisCopy(rowData.item.mainResource.resourceId)}</span>`;
}

export function cvesCountRenderer(rowData: { item: BreachPathIssueRowData }): TemplateResult {
  return html`<span class="cell-label">${rowData.item.cveCount ?? '-'}</span>`;
}

export function resourceTypeRenderer(rowData: { item: BreachPathIssueRowData }): TemplateResult {
  if (!rowData.item.mainResource.resourceType || rowData.item.mainResource.resourceType === AffectedDeviceType.NOT_AFFECTED) {
    return html`<span class="cell-label">${'-'}</span>`;
  }

  return html`
    <span class="cell-label">
      <div style="display: flex; align-items: center; gap: calc(var(--spacer) * 0.5);">
        <gz-breach-path-graph-icon
          name="${DEVICES_CLASS.get(rowData.item.mainResource.resourceType)}"
          color="var(--chart-302-icon-buttons-titles)"
        ></gz-breach-path-graph-icon>
        <gz-tooltip
          isEllipsis
          tooltipText="${BaseWebComponentTranslator.getInstance().getDeviceTypes(rowData.item.mainResource.resourceType)}"
        >
          <gz-text
            class="d-inline"
            fontSize="${FontSizes.PARAGRAPH_12}"
            fontWeight="${FontWeights.NORMAL}"
          >
            ${BaseWebComponentTranslator.getInstance().getDeviceTypes(rowData.item.mainResource.resourceType)}
          </gz-text>
        </gz-tooltip>
      </div>
    </span>
  `;
}

export function platformRenderer(rowData: { item: BreachPathIssueRowData }): TemplateResult {
  if (!rowData.item.operatingSystem || rowData.item.operatingSystem === OS_TYPE.UNKNOWN) {
    return html`<span class="cell-label">${'-'}</span>`;
  }

  return html`
    <span class="cell-label">
      <gz-tooltip
        tooltipText="${BaseWebComponentTranslator.getInstance().getOperatingSystem(rowData.item.operatingSystem)}"
      >
        <gz-breach-path-graph-icon
          name="ds-${OS_CLASS.get(rowData.item.operatingSystem)}"
          color="var(--chart-302-icon-buttons-titles)"
        ></gz-breach-path-graph-icon>
      </gz-tooltip>
    </span>
  `;
}

export function cloudAccountRenderer(rowData: { item: BreachPathIssueRowData }): TemplateResult {
  return html`<span class="cell-label">${rowData.item.cloudAccount}</span>`;
}

export function regionsRenderer(rowData: { item: BreachPathIssueRowData }): TemplateResult {
  return html`<span class="cell-label">${rowData.item.region}</span>`;
}

export function lastUpdatedRenderer(rowData: { item: BreachPathIssueRowData }): TemplateResult {
  const date = rowData.item.updatedAt;
  const formattedDate = date ? moment(date).format('DD MMM YYYY, HH:mm') : '-';

  return html`<span class="cell-label">${formattedDate}</span>`;
}

export function firstFoundRenderer(rowData: { item: BreachPathIssueRowData }): TemplateResult {
  const date = rowData.item.createdAt;
  const formattedDate = date ? moment(date).format('DD MMM YYYY, HH:mm') : '-';

  return html`<span class="cell-label">${formattedDate}</span>`;
}

export function actionsColumnRenderer(rowData: { item: BreachPathIssueRowData }): TemplateResult {
  const options = [];

  return selectColumnRenderer.call(this, rowData, options);
}
